<script setup lang="ts">
/**
 * /orders/:orderNo —— 运营端订单详情(v2 · O9;S-47 全新页)。
 *
 * ═══ 这一页与 O1 那张抽屉的关系 ═══
 *
 * O1(订单驾驶舱)的抽屉是「在列表里顺手看一眼」,这一页是**为某一单停下来看**:
 * 重心在右列整条高的「飞行节点凭证」——四张照片是 M-04 的兜底证据,
 * 运营核对纠纷时要一张一张点开看。入口只有一条:O1 抽屉头部那枚「查看完整详情 ›」,
 * 导航栏里没有这一格(Y-32:订单详情类页面不带导航项,check-admin-nav 的
 * NO_NAV_VIEWS 里写着理由)。
 *
 * ═══ 这一页吃两条接口 ═══
 *
 *   GET /api/admin/orders/{orderNo}        订单与计价快照(页面其余各行)
 *   GET /api/admin/orders/{orderNo}/proofs 四节点凭证(S-47 新开的只读口,Y-35)
 *
 * 第二条是本切片**先改契约、再写服务端、最后上页面**加出来的:运营端此前没有
 * 凭证读取口(客户侧 / 飞手侧 / 监管端各有一条),而 O9 整块凭证区正缺这个数。
 * 服务端复用 S-07 的 ProofService(投影与签名只有一份,三端看到的是同一张照片)。
 *
 * ═══ 概览八行的数据源(逐行都答得出出处)═══
 *
 * 行名与 M15(飞手作业详情)、G3(监管订单详情)同源,按画板自上而下:
 *   状态     = status(中文走 @sea-drone/ui 的 statusLabel 单点,标签与顶栏同一枚)
 *   路线     = origin.area → destination.area(区域没标注画「—」)
 *   距离     = priceSnapshot.distanceKm(契约里就有;整数公里,画板形态 `8 km`)
 *   承运商   = carrierName;承接飞手 = pilotName(都取自契约已有字段,空画「—」)
 *   下单时间 = createdAt(东八区、只到分钟)
 *   起飞时间 = **起飞那张凭证的时刻**(takenAt 优先、退 uploadedAt;先例 AS-080③ ——
 *             飞手侧「作业详情」的起飞时间就是同一个口径)。OrderDetail 里没有
 *             任何时间字段能答这一行,而凭证区恰好把它答了;取不到画「—」。
 *   凭证进度 = 已留证的节点数 / 4 —— 分母走共享包的 PROOF_NODE,不写死 4;
 *             写法与 M15 的 `proofProgressText` 同一形态(数字算出来、分母走单点)。
 *
 * ═══ 画板上有、这里没有的四件(都不建死按钮,登记在 design-copy 的 O9 帧里)═══
 *
 * 「导出证据包」没有接口(全仓搜不到证据包导出/下载的路由);「查看大图 ›」没有落点
 * (没有大图页也没有路由);「铃铛」没有消息中心(ops 侧没有消息接口);
 * 「管理员」头像与姓名没有来源(登录返回体只有 username,没有姓名也没有头像)。
 * 凭证照片本身**点得开**:缩略图是一枚指向现签 URL 的锚点,新标签页看原图 ——
 * 与监管端订单详情「打开照片」同一种做法,不是死交互。
 *
 * ═══ 「本期没有视频」那句话不渲染(照抄录文件的处置)═══
 *
 * 画板凭证卡底部那行「本期没有视频,这是事先讲好的空。」读起来是对实现者说的
 * (视频整块不在本期:L0-06 搁置、M-09 排除),登记进 design-copy 的 O9 帧豁免;
 * 要不要在运营界面上明说「为什么没有视频」需要一条裁决 —— 监管端那半句
 * (「视频直播、三天回放……不在本期交付范围」)已在 G3 的页面里说清。
 *
 * ═══ 照片打不开时说两句不同的话 ═══
 *
 * 「签名过期」与「加载失败」是两件事(照 gov 订单详情的口径):本地 STORAGE_MODE=stub
 * 时桶域名根本不存在,照片**永远**加载不出来,把所有打不开一律说成「链接已过期,
 * 刷新重新签发」在演示现场就是一句假话。所以过期画「链接已过期…」(刷新本页会重签)、
 * 加载失败画「照片暂不可用」(原因不猜)。
 */
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useMessage } from 'naive-ui'
import {
  ErrorBanner,
  FieldGrid,
  NavIcon,
  PanelCard,
  StatusTag,
  proofNodeLabel,
} from '@sea-drone/ui'
import { PROOF_NODE, REVIEW_RATING_MAX, formatShanghaiMinute, reviewAuthorRoleLabel, signatureViaLabel } from '@sea-drone/shared'
import { api, ApiError, type AdminOrderDetail, type Proof } from '../api/client'

const props = defineProps<{ orderNo: string }>()

/** 这一页是运营端深色家族(O9 与 O1 同壳),底座由 S-41 的第三档 tone 给 */
const TONE = 'ops-dark' as const

const router = useRouter()
const message = useMessage()

const order = ref<AdminOrderDetail | null>(null)
/**
 * 这一单的凭证(每个节点当前那一张)。**未留证的节点不在这里** ——
 * 契约的返回体只给已登记的行(不是给一行空值),下面按 PROOF_NODE 补「待留证」。
 */
const proofs = ref<Proof[]>([])
const lastError = ref<string | null>(null)
/** 已经加载失败的缩略图(按 fileKey 记)。@error 只触发一次,不记下来就会一直重试破图 */
const brokenPhotos = ref<Set<string>>(new Set())

function show(e: unknown): void {
  const msg =
    e instanceof ApiError
      ? `[${e.code}] ${e.message}（requestId ${e.requestId}）`
      : e instanceof Error
        ? e.message
        : String(e)
  lastError.value = msg
  message.error(msg, { duration: 8000 })
}

async function load(): Promise<void> {
  try {
    const [detail, proofList] = await Promise.all([
      api.getOrder(props.orderNo),
      api.getOrderProofs(props.orderNo),
    ])
    order.value = detail
    proofs.value = proofList.items
    brokenPhotos.value = new Set()
    lastError.value = null
  } catch (e) {
    order.value = null
    proofs.value = []
    show(e)
  }
}

/** 单号不存在或接口失败时用同一句人话回列表 —— 404 的那句话由错误条原样显示 */
function goBack(): void {
  void router.push('/orders')
}

/** 承运商 / 飞手 / 区域为空时画「—」—— 空着读起来像「这里没有这一项」,它只是还没有值 */
function orDash(value: string | null): string {
  return value ?? '—'
}

/**
 * 运营端代确认收货(S-55,Y-47)。**这是替收件人做的一个决定** ——
 * 签收一旦成为完成的闸门,就必须有人能在收件人不动的时候把闸门推开。
 * 服务端把签收方式记 ops 并写审计日志;成功后用返回的订单刷新这一页。
 */
async function confirmReceipt(): Promise<void> {
  if (order.value === null || confirming.value) return
  confirming.value = true
  try {
    order.value = await api.confirmReceiptByOps(props.orderNo)
    lastError.value = null
    message.success('已代确认收货（签收方式记「运营代确认」，已留审计日志）')
  } catch (e) {
    show(e)
  } finally {
    confirming.value = false
  }
}

const routeText = computed(() => {
  const o = order.value
  if (o === null) return '—'
  return `${orDash(o.origin.area)} → ${orDash(o.destination.area)}`
})

/**
 * 起飞时间 = 起飞那张凭证的时刻(takenAt 优先、退到 uploadedAt)。
 *
 * takenAt 是飞手手机上的时刻(契约注明它不参与任何判断),uploadedAt 才是可信的
 * 登记时刻;两处**优先序一致**很重要 —— 与飞手侧 M15 的「起飞时间」取的是同一个数,
 * 同一单在两个端显示两个不同的起飞时刻,读起来两边都很正常(先例 AS-080③)。
 */
const takeoffText = computed(() => {
  const p = proofs.value.find((x) => x.node === 'took_off')
  if (p === undefined) return '—'
  return formatShanghaiMinute(p.takenAt ?? p.uploadedAt)
})

/** 已留证的节点数(画板「2 / 4 已留证」的 2;分母是共享包的节点清单) */
const provedCount = computed(() => proofs.value.length)

/**
 * 签收那一行的值(S-55,Y-44 / Y-47):没签过画「未签收」,签过画「时刻(方式)」。
 *
 * 方式走共享包的 signatureViaLabel 单点(user 收件人自己点 / auto 到点自动 / ops 运营代确认)——
 * 页面上不该出现 user / auto / ops 原始码。签收是「完成」的闸门,运营要一眼看得出这一单签没签。
 */
const signedText = computed(() => {
  const o = order.value
  if (o === null) return '—'
  if (o.signedAt === null) return '未签收'
  const via = o.signedVia === null ? '方式未知' : signatureViaLabel(o.signedVia)
  return `${formatShanghaiMinute(o.signedAt)}（${via}）`
})

/** 还没签收才让运营点「代确认收货」—— 签过的单再点没有意义(服务端也是幂等的) */
const canConfirmReceipt = computed(() => order.value !== null && order.value.signedAt === null)
const confirming = ref(false)

/**
 * 星级串(`★★★★☆`)。rating 是 1–5 的整数(契约 Review.rating);认不出来的值夹回区间,
 * 不抛也不画一串越界的星。分母走共享包的 REVIEW_RATING_MAX,不写死 5。
 */
function starsOf(rating: number): string {
  const n = Math.max(0, Math.min(REVIEW_RATING_MAX, Math.round(rating)))
  return '★'.repeat(n) + '☆'.repeat(REVIEW_RATING_MAX - n)
}

/**
 * 这一单的评价(S-57 / Y-52)。运营端订单详情返回**全部**两条(下单人评飞手 + 飞手评下单人),
 * 没评过时是空数组 —— 空态画一句「这一单还没有评价」,不画一张空卡(空卡读起来像渲染坏了)。
 * 作者是谁由 reviewAuthorRoleLabel 说清(下单人 / 飞手),页面上不出现 customer / pilot 原始码。
 */
const reviewRows = computed(() =>
  (order.value?.reviews ?? []).map((r) => ({
    who: reviewAuthorRoleLabel(r.authorRole),
    stars: starsOf(r.rating),
    content: r.content,
  })),
)

/** 一切「N / 4 已留证」的写法只有这一处(与 M15 的 proofProgressText 同一形态) */
const proofProgressText = computed(() => `${provedCount.value} / ${PROOF_NODE.length} 已留证`)

/** 概览卡其余七行。空值交给 FieldGrid 画「—」 */
const overviewFields = computed(() => {
  const o = order.value
  if (o === null) return []
  return [
    { label: '路线', value: routeText.value },
    { label: '距离', value: `${o.priceSnapshot.distanceKm} km` },
    { label: '承运商', value: orDash(o.carrierName) },
    { label: '承接飞手', value: orDash(o.pilotName) },
    { label: '下单时间', value: formatShanghaiMinute(o.createdAt) },
    { label: '起飞时间', value: takeoffText.value },
    { label: '签收', value: signedText.value },
    { label: '凭证进度', value: proofProgressText.value },
  ]
})

/**
 * 凭证卡四行:**按 PROOF_NODE 的履约次序**(取货 → 起飞 → 送达 → 空机返回),
 * 不是按接口返回的次序、也不是按上传时间 —— 节点名走 proofNodeLabel 单点
 * (画板写「空机返回」,单点表里是「无人机空机返回」,以单点为准:
 * 同一枚节点在另三个端与服务端错误话术里都是那个长名字)。
 *
 * 已留证:副行给时刻(形态与画板一致 `2026-10-15 14:18`;takenAt 优先、退 uploadedAt
 * —— 与「起飞时间」同一处口径)、缩略图给现签 URL;
 * 待留证:副行写「待留证」、缩略图位画一枚「—」。
 */
const proofRows = computed(() => {
  const byNode = new Map(proofs.value.map((p) => [p.node, p]))
  return PROOF_NODE.map((node) => {
    const p = byNode.get(node)
    return {
      node,
      label: proofNodeLabel(node),
      proved: p !== undefined,
      sub: p === undefined ? '待留证' : formatShanghaiMinute(p.takenAt ?? p.uploadedAt),
      proof: p ?? null,
    }
  })
})

/** 航线示意卡的两枚 chip:地名取自订单的 origin / destination,没标注画「未标注」 */
const originName = computed(() => order.value?.origin.area ?? '未标注')
const destinationName = computed(() => order.value?.destination.area ?? '未标注')
const distanceChip = computed(() => `${order.value?.priceSnapshot.distanceKm ?? '—'} km`)

/** 链接过期用既有的判断(urlExpiresAt 早于此刻)—— 与监管端订单详情同一条 */
function expired(p: Proof): boolean {
  return new Date(p.urlExpiresAt).getTime() < Date.now()
}

/**
 * 图片加载失败与「签名过期」是两件事,不能合并成一句话(见文件头)。
 * 这里只说事实:照片暂不可用。原因不猜。
 */
function markBroken(fileKey: string): void {
  brokenPhotos.value.add(fileKey)
}

// 概览卡的顶部横幅是**装饰**(画板上一张码头照片,不带任何数据与文案)。
// 复用 S-42 拷进 public/ 的那张演示照片(侧栏海报同一张)—— 不为一块装饰再入一份资源。
const HERO_SRC = '/home-hero.png'

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page">
    <!--
      页面级顶栏(画板 TopBar,不是全局壳的):返回 + 单号 + 状态标签。
      右端画板的「导出证据包」/ 铃铛 / 管理员三件没有接口也没有数据来源
      (见文件头那段),不渲染 —— 死按钮与假头像比空着更坏
    -->
    <div class="topbar">
      <button type="button" class="back" title="返回订单列表" @click="goBack">
        <NavIcon name="back" :size="20" />
      </button>
      <span class="order-no">{{ props.orderNo }}</span>
      <StatusTag v-if="order" :tone="TONE" :status="order.status" />
      <!--
        代确认收货(S-55,Y-47):只有还没签收的单才出现这一枚。它是兜底 ——
        收件人不注册 / 手机号填错 / 一直不点,运营替他把完成的闸门推开。
        签过的单再点没有意义(服务端幂等),所以签过就不画
      -->
      <button
        v-if="canConfirmReceipt"
        type="button"
        class="confirm-receipt"
        :disabled="confirming"
        @click="confirmReceipt"
      >
        {{ confirming ? '确认中…' : '代确认收货' }}
      </button>
    </div>

    <ErrorBanner v-if="lastError" :tone="TONE" :message="lastError" title="加载失败" />

    <div v-if="order" class="detail">
      <div class="left">
        <!-- 订单概览:横幅 + 八行(状态行的值是一枚标签,其余七行走 FieldGrid) -->
        <PanelCard :tone="TONE" padding="none">
          <img class="hero" :src="HERO_SRC" alt="" />
          <div class="overview">
            <div class="ov-row">
              <span class="ov-label">状态</span>
              <StatusTag :tone="TONE" :status="order.status" size="sm" />
            </div>
            <FieldGrid :tone="TONE" inline :columns="1" :items="overviewFields" />
          </div>
        </PanelCard>

        <!--
          航线示意卡。底图是演示资源(g5-map.png,仓库根拷进 public/);
          折线与三枚 chip 的位置是**手摆的示意坐标**(装饰,不吃数据,先例 AS-078)——
          但 chip 里的字是订单的真字段:起点 / 终点 = origin / destination.area(空画
          「未标注」),里程 = priceSnapshot.distanceKm。画板那枚「查看大图 ›」没有落点
          (没有大图页也没有路由),不渲染,登记在 design-copy 的 O9 帧里。
        -->
        <PanelCard :tone="TONE" title="航线示意">
          <div class="map">
            <img class="map-img" src="/g5-map.png" alt="" />
            <svg class="map-line" viewBox="0 0 490 180" preserveAspectRatio="none" aria-hidden="true">
              <path d="M36 142 C 130 132, 200 100, 292 88 S 430 62, 456 40" />
            </svg>
            <span class="map-chip map-chip-start">
              <span class="map-dot map-dot-start" />{{ originName }}
            </span>
            <span class="map-chip map-chip-end">
              <span class="map-dot map-dot-end" />{{ destinationName }}
            </span>
            <span class="map-chip map-chip-km">{{ distanceChip }}</span>
          </div>
        </PanelCard>

        <!--
          评价区(S-58,Y-52):运营端能看到这一单的两条(下单人评飞手 + 飞手评下单人)。
          当事人两侧的详情没有这一块 —— 那条带盲评过滤的读取只返回对调用方可见的几条,
          运营端走 /api/admin/orders/{orderNo} 才拿得到全部。没评过时画一句空态,不画空卡。
        -->
        <PanelCard :tone="TONE" title="评价">
          <p v-if="reviewRows.length === 0" class="review-empty">这一单还没有评价</p>
          <ul v-else class="reviews">
            <li v-for="(r, i) in reviewRows" :key="i" class="review-row">
              <div class="review-head">
                <span class="review-who">{{ r.who }}</span>
                <span class="review-stars">{{ r.stars }}</span>
              </div>
              <p v-if="r.content" class="review-text">{{ r.content }}</p>
            </li>
          </ul>
        </PanelCard>
      </div>

      <!--
        右列整条高:飞行节点凭证(这一页的重心)。四行 = 四个节点,
        已留证的给照片与时刻,待留证的给空框与「待留证」。
        缩略图是一枚锚点:点开新标签页看**现签的原图**(与监管端「打开照片」同一种做法);
        「查看大图 ›」那种没有落点的入口不建(design-copy 的 O9 帧里记着)
      -->
      <PanelCard :tone="TONE" title="飞行节点凭证">
        <template #extra>
          <!-- 画板右端那行小字:它说的是这套证据的规则,不是装饰 -->
          <span class="proof-head-note">拍摄即留证,不可补拍或替换</span>
        </template>

        <ol class="proofs">
          <li v-for="row in proofRows" :key="row.node" class="proof-row">
            <span class="proof-dot" :class="{ done: row.proved }" aria-hidden="true" />
            <span class="proof-text">
              <span class="proof-node" :class="{ done: row.proved }">{{ row.label }}</span>
              <span class="proof-sub">{{ row.sub }}</span>
            </span>

            <!-- 三种状态互斥,而且是**替换**不是叠加:img 失败就从树里摘掉,不留浏览器那张破图 -->
            <template v-if="row.proof && !expired(row.proof) && !brokenPhotos.has(row.proof.fileKey)">
              <a
                class="proof-thumb"
                :href="row.proof.url"
                target="_blank"
                rel="noreferrer"
                title="打开原图"
              >
                <img
                  class="proof-img"
                  :src="row.proof.url"
                  alt="节点凭证照片"
                  loading="lazy"
                  @error="markBroken(row.proof.fileKey)"
                />
              </a>
            </template>
            <div v-else-if="row.proof" class="proof-slot">
              <span class="proof-lost">
                {{ expired(row.proof) ? '链接已过期，刷新本页重新签发' : '照片暂不可用' }}
              </span>
            </div>
            <div v-else class="proof-empty" aria-hidden="true">—</div>
          </li>
        </ol>
      </PanelCard>
    </div>
  </div>
</template>

<style scoped>
/* 页内纵向节奏:画板是 16 —— 这里由 .page 的 gap 一处给 */
.page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── 顶栏 ───────────────────────────────────────────── */

.topbar {
  display: flex;
  align-items: center;
  gap: 12px;
}

.back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  border-radius: var(--sd-radius-sm);
  background: transparent;
  color: var(--sd-text2);
  cursor: pointer;
}

.back:hover {
  color: var(--sd-text1);
  background: var(--sd-surface-alt);
}

.order-no {
  font-family: var(--sd-font-mono);
  font-size: 20px;
  font-variant-numeric: tabular-nums;
  color: var(--sd-text1);
}

/* 代确认收货:顶栏右端的一枚次要按钮(签收是完成闸门,运营要一眼看到并够得着) */
.confirm-receipt {
  margin-left: auto;
  padding: 6px 12px;
  border: 1px solid var(--sd-border);
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
  color: var(--sd-text1);
  font-size: 13px;
  cursor: pointer;
}

.confirm-receipt:hover:not(:disabled) {
  border-color: var(--sd-brand);
  color: var(--sd-brand);
}

.confirm-receipt:disabled {
  cursor: default;
  opacity: 0.6;
}

/* ── 双列(左 560:概览 + 航线;右列整条高:凭证)───────── */

.detail {
  display: grid;
  grid-template-columns: minmax(0, 560px) minmax(0, 1fr);
  gap: 16px;
}

.left {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── 订单概览卡 ─────────────────────────────────────── */

/* 横幅只有上面两角圆(画板)—— 卡片本身的描边 1px,所以圆角收小一档贴住 */
.hero {
  display: block;
  width: 100%;
  height: 110px;
  object-fit: cover;
  border-radius: calc(var(--sd-radius-lg) - 1px) calc(var(--sd-radius-lg) - 1px) 0 0;
}

.overview {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

/* 状态行:名左值右,与下面 FieldGrid 的 inline 档同一套节奏 */
.ov-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ov-label {
  font-size: 12px;
  color: var(--sd-text3);
}

/* ── 航线示意卡 ─────────────────────────────────────── */

.map {
  position: relative;
  height: 210px;
  border-radius: var(--sd-radius-md);
  overflow: hidden;
}

.map-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 折线是装饰(手摆的示意坐标,不吃数据)。颜色走令牌,不写裸色值 */
.map-line {
  position: absolute;
  inset: 10px;
  width: calc(100% - 20px);
  height: calc(100% - 20px);
  pointer-events: none;
}

.map-line path {
  fill: none;
  stroke: var(--sd-brand);
  stroke-width: 2;
  stroke-linecap: round;
}

.map-chip {
  position: absolute;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 8px;
  border-radius: 3px;
  font-size: 11px;
  color: var(--sd-text1);
  /* 深底 chip:同一枚底图上的三枚 chip 共用一个底,不另开一个「差不多的黑」 */
  background: var(--sd-bg-deep);
  opacity: 0.92;
}

.map-chip-start {
  left: 7%;
  bottom: 22%;
}

.map-chip-end {
  right: 18%;
  top: 16%;
}

.map-chip-km {
  left: 34%;
  bottom: 14%;
  color: var(--sd-brand);
}

.map-dot {
  width: 8px;
  height: 8px;
  flex: none;
  border-radius: var(--sd-radius-pill);
}

.map-dot-start {
  background: var(--sd-success);
}

.map-dot-end {
  background: var(--sd-brand);
}

/* ── 评价卡(S-58,Y-52)────────────────────────────── */

/* 没评过时的空态:一句三级灰的话,不画一张空卡(空卡读起来像渲染坏了) */
.review-empty {
  margin: 0;
  font-size: 13px;
  color: var(--sd-text3);
}

.reviews {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* 一条评价:浅底一块(与凭证行同一族),头部「作者 + 星级」,下面一句文字(选填) */
.review-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
}

.review-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.review-who {
  font-size: 13px;
  color: var(--sd-text3);
}

/* 星级:金黄(语义 warning),与小程序那一侧的选中星同一个色族 */
.review-stars {
  font-size: 14px;
  color: var(--sd-warning);
  letter-spacing: 2px;
}

.review-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: var(--sd-text1);
}

/* ── 凭证卡 ─────────────────────────────────────────── */

.proof-head-note {
  font-size: 12px;
  color: var(--sd-text3);
  white-space: nowrap;
}

.proofs {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.proof-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px;
  border-radius: var(--sd-radius-sm);
  background: var(--sd-surface-alt);
}

/* 10×10 的状态圆点:已留证绿、未留证三级灰(画板两色) */
.proof-dot {
  width: 10px;
  height: 10px;
  flex: none;
  border-radius: var(--sd-radius-pill);
  background: var(--sd-text3);
}

.proof-dot.done {
  background: var(--sd-success);
}

.proof-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.proof-node {
  font-size: 14px;
  color: var(--sd-text3);
}

.proof-node.done {
  color: var(--sd-text1);
}

.proof-sub {
  font-size: 12px;
  color: var(--sd-text3);
  font-variant-numeric: tabular-nums;
}

/* 缩略图 96×64:照片来没来,版面都不跳 */
.proof-thumb,
.proof-slot,
.proof-empty {
  box-sizing: border-box;
  width: 96px;
  height: 64px;
  flex: none;
  border-radius: var(--sd-radius-sm);
}

.proof-thumb {
  display: block;
}

.proof-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: var(--sd-radius-sm);
}

.proof-slot {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  text-align: center;
  background: var(--sd-bg-deep);
  border: 1px solid var(--sd-border);
}

.proof-lost {
  font-size: 11px;
  line-height: 1.5;
  color: var(--sd-text3);
}

/* 未留证的空框:深底 + 描边 + 居中一枚「—」(画板形态) */
.proof-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  color: var(--sd-text3);
  background: var(--sd-bg-deep);
  border: 1px solid var(--sd-border);
}

@media (max-width: 1200px) {
  .detail {
    grid-template-columns: 1fr;
  }
}
</style>
